// store/languageSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

// common
import getGeolocation from 'common/get-geolocation';
import getBrowserLanguage from 'common/get-browser-language';
import getInitialLanguage from 'common/get-initial-language'

interface LanguageState {
  current: string;
  available: string[];
  isLoading: boolean;
  detectionSource: 'localStorage' | 'browser' | 'geo' | 'default' | null;
  geoCountry: string | null;
  browserLang: string | null;
}

// Language mapping based on country codes
const countryToLanguageMap: Record<string, string> = {
  'TH': 'th',      // Thailand
  'MY': 'ms',      // Malaysia
  'SG': 'en-SG',   // Singapore
  'HK': 'zh-HK',   // Hong Kong
  'TW': 'zh-TW',   // Taiwan
  'CN': 'zh-CN',   // China
  'JP': 'ja',      // Japan
  'US': 'en-US',   // United States
  'GB': 'en',      // United Kingdom
  'AU': 'en',      // Australia
  'CA': 'en',      // Canada (could be 'en' or 'fr')
  'NZ': 'en',      // New Zealand
  'IN': 'en',      // India
  'PH': 'en',      // Philippines
};

// Function to normalize language code
const normalizeLanguageCode = (langCode: string, countryCode?: string): string => {
  const supportedLangs = ['en', 'th', 'ms', 'zh-TW', 'zh-CN', 'zh-HK', 'en-SG', 'en-US', 'ja'];

  // Handle full language codes like 'en-US', 'zh-TW'
  if (supportedLangs.includes(langCode)) {
    return langCode;
  }

  // Extract base language (e.g., 'en' from 'en-US')
  const baseLang = langCode.split('-')[0].toLowerCase();

  // Special handling for Chinese variants
  if (baseLang === 'zh') {
    if (countryCode === 'TW') return 'zh-TW';
    if (countryCode === 'HK') return 'zh-HK';
    if (countryCode === 'CN') return 'zh-CN';
    // Default to Traditional Chinese
    return 'zh-TW';
  }

  // Special handling for English variants
  if (baseLang === 'en') {
    if (countryCode === 'SG') return 'en-SG';
    if (countryCode === 'US') return 'en-US';
    // Default to global English
    return 'en';
  }

  // Check if base language is supported
  if (supportedLangs.includes(baseLang)) {
    return baseLang;
  }

  return 'en'; // Default fallback
};

// Async thunk to detect language with full logic
export const detectLanguage = createAsyncThunk(
  'language/detectLanguage',
  async (_, { rejectWithValue }) => {
    try {
      // Priority 1: Check LocalStorage
      const storedLang = localStorage.getItem('lang');
      if (storedLang) {
        return {
          language: storedLang,
          source: 'localStorage' as const,
          browserLang: getBrowserLanguage(),
          geoCountry: null
        };
      }

      // Priority 2: Get browser language
      const browserLangRaw = getBrowserLanguage();
      const browserLang = browserLangRaw ? normalizeLanguageCode(browserLangRaw) : null;

      // Priority 3: Get geolocation
      const geoData = await getGeolocation();
      const geoCountryCode = geoData?.countryCode || null;
      const geoLang = geoCountryCode ? countryToLanguageMap[geoCountryCode] : null;

      // Decision logic
      let selectedLang = 'en';
      let source: 'browser' | 'geo' | 'default' = 'default';

      if (geoLang) {
        // Use geolocation if available
        selectedLang = geoLang;
        source = 'geo';
      } else if (browserLang) {
        // Fallback to browser language
        selectedLang = browserLang;
        source = 'browser';
      } else {
        // Ultimate fallback to English
        selectedLang = 'en';
        source = 'default';
      }

      // Save to LocalStorage
      localStorage.setItem('lang', selectedLang);

      return {
        language: selectedLang,
        source,
        browserLang,
        geoCountry: geoData?.country || null
      };

    } catch (error) {
      console.error('❌ Error in detectLanguage:', error);
      return rejectWithValue('Failed to detect language');
    }
  }
);

const initialState: LanguageState = {
  current: getInitialLanguage(),
  available: ['en', 'th', 'ms', 'zh-TW', 'zh-CN', 'zh-HK', 'en-SG', 'en-US', 'ja'],
  isLoading: false,
  detectionSource: null,
  geoCountry: null,
  browserLang: null
};

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    setLanguage: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
      state.detectionSource = 'localStorage';
      // Sync to LocalStorage
      localStorage.setItem('lang', action.payload);
    },
    clearLanguagePreference: (state) => {
      localStorage.removeItem('lang');
      state.current = 'en';
      state.detectionSource = 'default';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(detectLanguage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(detectLanguage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload.language;
        state.detectionSource = action.payload.source;
        state.browserLang = action.payload.browserLang;
        state.geoCountry = action.payload.geoCountry;
        console.log('✅ Language detection complete:', {
          language: action.payload.language,
          source: action.payload.source,
          browserLang: action.payload.browserLang,
          geoCountry: action.payload.geoCountry
        });
      })
      .addCase(detectLanguage.rejected, (state, action) => {
        state.isLoading = false;
        state.current = 'en';
        state.detectionSource = 'default';
        console.error('❌ Language detection failed:', action.payload);
      });
  }
});

export const { setLanguage, clearLanguagePreference } = languageSlice.actions;
export default languageSlice.reducer;